import {
  CampaignDatePresets,
  campaignFilterMap,
  CampaignFilterParam,
  CampaignFilterType,
  CampaignType,
} from '@retail/campaign/types';
import { useSearchParams } from 'react-router';
import { CampaignsQueryParams } from '@retail/campaign/data-access';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

export const useCampaignsSearchParams = (
  campaignFilters: CampaignFilterType[]
): CampaignsQueryParams => {
  const [searchParams] = useSearchParams();
  const availableFilterParams = useMemo(
    () =>
      new Set(
        Object.entries(campaignFilterMap)
          .filter(([filter]) => campaignFilters.includes(filter as CampaignFilterType))
          .flatMap(([_, params]) => params)
      ),
    [campaignFilters]
  );

  const getParam = useCallback(
    (param: CampaignFilterParam) =>
      availableFilterParams.has(param) ? searchParams.get(param) : null,
    [availableFilterParams, searchParams]
  );

  const mandatoryValue = getParam('mandatory');
  const brandsValue = getParam('brands');
  const datePresetParam = getParam('date_preset');

  const { startDate, endDate } = useMemo(() => {
    const today = dayjs();
    const todayString = today.format('YYYY-MM-DD');
    const datePreset = CampaignDatePresets.parse(datePresetParam);
    switch (datePreset) {
      case 'PAST':
        return { startDate: undefined, endDate: todayString };
      case 'CURRENT_AND_UPCOMING':
      default:
        return { startDate: todayString, endDate: undefined };
    }
  }, [datePresetParam]);

  return useMemo(
    () => ({
      search: getParam('search') ?? undefined,
      campaignType: (getParam('campaign_type') as CampaignType) ?? undefined,
      brandKeys: brandsValue?.split(','),
      startDate,
      endDate,
      mandatory: mandatoryValue === 'false' ? false : mandatoryValue === 'true' ? true : undefined,
    }),
    [getParam, brandsValue, startDate, endDate, mandatoryValue]
  );
};
